import { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { Heart, HeartHandshake, LifeBuoy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { appId, db } from '@/lib/firebase';
import { createBillingPortalSession } from '@/lib/stripe-billing';
import { MonthlyWarriorTeaser } from '@/components/donate/MonthlyWarriorTeaser';
import { type DonorRollupTimestamp, useDonorRollup } from '@/lib/use-donor-rollup';
import { useWarriorNumber } from '@/lib/use-warrior-number';

interface DonorDashboardPageProps {
  userId: string;
  displayName: string;
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDate(ts: DonorRollupTimestamp | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function DonorDashboardPage({ userId, displayName }: Readonly<DonorDashboardPageProps>): JSX.Element {
  const { donorRecord, loading, loadError } = useDonorRollup(userId, 'DonorDashboardPage');
  const { warriorNumber } = useWarriorNumber(userId, 'DonorDashboardPage');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    const subscriptionsQuery = query(
      collection(db, 'artifacts', appId, 'users', userId, 'subscriptions'),
      where('status', 'in', ['active', 'trialing']),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      subscriptionsQuery,
      (snapshot) => {
        setHasActiveSubscription(!snapshot.empty);
      },
      () => {
        setHasActiveSubscription(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const handleOpenBillingPortal = async (): Promise<void> => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const result = await createBillingPortalSession(`${globalThis.location.origin}/dashboard/donor`);
      globalThis.open(result.portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error(`[DonorDashboardPage] Failed to open billing portal for user "${userId}":`, error);
      setPortalError('Unable to open billing portal right now. Please try again in a moment.');
    } finally {
      setPortalLoading(false);
    }
  };

  const impactMessage = useMemo(() => {
    if (!donorRecord) return 'Every donation directly helps CKD warriors and their families.';
    if (donorRecord.monthlyDonationCount > 0) {
      return 'Your recurring support creates reliable monthly impact for CKD warriors.';
    }
    return 'Thank you for stepping in with one-time support when families need it most.';
  }, [donorRecord]);

  const showManageMonthly =
    hasActiveSubscription || donorRecord?.lastDonationFrequency === 'monthly';

  if (loading) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <section className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">
          Loading your donor dashboard...
        </section>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <section className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-700 font-medium">{loadError}</p>
          <p className="mt-2 text-sm text-red-600">You can still continue supporting via the donation page.</p>
          <Link to="/donate" className="inline-flex mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            Donate Again
          </Link>
        </section>
      </main>
    );
  }

  if (!donorRecord) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-10">
        <section className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
          <HeartHandshake size={24} className="mx-auto mb-3 text-blue-700" />
          <h1 className="text-2xl font-semibold text-gray-900">Welcome, {displayName}</h1>
          <p className="mt-2 text-gray-600">
            We are preparing your donor dashboard. Your donation record will appear here shortly.
          </p>
          <Link to="/donate" className="inline-flex mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            Support Again
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Donor Dashboard</p>
        <h1 className="mt-2 text-3xl font-bold text-gray-900">Thank you, {displayName}</h1>
        <p className="mt-3 text-gray-600">{impactMessage}</p>
        {warriorNumber != null && donorRecord.lastDonationFrequency === 'monthly' ? (
          <div className="mt-4">
            <MonthlyWarriorTeaser warriorNumber={warriorNumber} />
          </div>
        ) : null}
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <article className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Lifetime Donation</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {formatMoney(donorRecord.totalDonated, donorRecord.currency)}
          </p>
        </article>
        <article className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Donations</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{donorRecord.donationCount}</p>
        </article>
        <article className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last Donation</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {formatMoney(donorRecord.lastDonationAmount, donorRecord.currency)}
          </p>
        </article>
        <article className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last Donated On</p>
          <p className="mt-2 text-base font-semibold text-gray-900">{formatDate(donorRecord.lastDonationAt)}</p>
        </article>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <article className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <Heart size={18} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Donation Mix</h2>
          </div>
          <div className="mt-4 space-y-2 text-sm text-gray-700">
            <p>Monthly donations: {donorRecord.monthlyDonationCount}</p>
            <p>One-time donations: {donorRecord.oneTimeDonationCount}</p>
            <p>
              Current donor type:{' '}
              <span className="font-medium">
                {donorRecord.lastDonationFrequency === 'monthly' ? 'Monthly donor' : 'One-time donor'}
              </span>
            </p>
          </div>
        </article>
        <article className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <LifeBuoy size={18} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Next Actions</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/donate" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              Donate Again
            </Link>
            {showManageMonthly ? (
              <button
                type="button"
                onClick={() => {
                  void handleOpenBillingPortal();
                }}
                disabled={portalLoading}
                className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
              >
                {portalLoading ? 'Opening Billing Portal...' : 'Manage Monthly Donation'}
              </button>
            ) : null}
            <Link to="/warriors" className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Meet CKD Warriors
            </Link>
          </div>
          {showManageMonthly ? (
            <p className="mt-3 text-xs text-gray-600">
              Active monthly donation. Manage card, change amount, or cancel anytime.
            </p>
          ) : null}
          {portalError ? <p className="mt-2 text-xs text-red-600">{portalError}</p> : null}
        </article>
      </section>
    </main>
  );
}
